// The storefront page registry — ONE table that drives everything a page
// needs to exist: its routes (App.jsx, office + masked hosts), its SEO
// defaults (seoHead.jsx and the API's crawler/head prerender), the Custom
// CSS / Custom HTML scope pickers, robots.txt and the sitemap.
//
// storefrontPages.json is the source of truth. The Go API embeds a COPY
// (mlm-vii-api/internal/api/storefront_pages.json) — run `npm run sync:pages`
// after editing and commit both repos. Adding a page is now: one JSON entry
// here + the named export / dispatcher case in storefront.jsx.
//
// Entry fields:
//   key        the `page` string the Storefront dispatcher threads to every
//              Shell — also the customCss/customHtml scope ('landing' → 'home')
//   path       store-relative URL (may carry :params); `paths` lists extras
//   label      human label; the default <title> is "<label> | <site title>"
//   export     named export in storefront.jsx (or `module` for another file)
//   templates  template ids that implement the page; absent = every template.
//              On a template that lacks it the route renders Home, so both
//              heads mark it noindex + canonical "/" instead of publishing a
//              duplicate of the homepage under a second URL.
//   private    transactional/account flow — always noindex, never in the
//              sitemap, Disallow'd in robots.txt
//   title / description / ogType   per-page SEO defaults; {store} = store
//              name, {siteTitle} = store SEO title, {label} = label
import PAGES from './storefrontPages.json';

export const STOREFRONT_PAGES = PAGES;

const byKey = new Map(PAGES.map((p) => [p.key, p]));
export const pageByKey = (key) => byKey.get(key) ?? null;

// customCss/customHtml scope for a page key ('landing' is 'home' to admins).
export const pageScope = (page) => (page === 'landing' ? 'home' : (page || 'other'));

export const implementedOn = (entry, templateId) =>
  !!entry && (!Array.isArray(entry.templates) || entry.templates.includes(String(templateId ?? '')));

export const isPrivatePage = (key) => pageByKey(key)?.private === true;

const allPaths = (entry) => (Array.isArray(entry.paths) && entry.paths.length ? entry.paths : [entry.path]);

// Route rows for App.jsx — every page except the index (landing), each
// [path, export, module]. Order is the registry order; the :webalias catch-all
// is appended by the router itself and MUST stay last.
export const routeRows = () => PAGES.filter((p) => p.key !== 'landing')
  .flatMap((p) => allPaths(p).map((path) => [path, p.export, p.module ?? 'storefront']));

// Store-relative sub path for a page key + route params: '' for home,
// 'product/C-ada' for a product, 'hrt/men' for a static page.
export const subForPage = (page, params = {}) => {
  const entry = pageByKey(page);
  if (!entry) return '';
  return String(entry.path ?? '').replace(/:([A-Za-z]+)/g, (_, name) => encodeURIComponent(String(params[name] ?? '')));
};

// Inverse: which registry page serves a store-relative sub path. Static paths
// win; :param paths match segment-wise. Returns { entry, params } or null.
export const matchSub = (sub) => {
  const segs = String(sub ?? '').replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  if (!segs.length) return { entry: pageByKey('landing'), params: {} };
  let dynamic = null;
  for (const entry of PAGES) {
    for (const path of allPaths(entry)) {
      const parts = String(path).split('/').filter(Boolean);
      if (parts.length !== segs.length) continue;
      const params = {};
      let ok = true;
      for (let i = 0; i < parts.length; i++) {
        if (parts[i].startsWith(':')) params[parts[i].slice(1)] = decodeURIComponent(segs[i]);
        else if (parts[i].toLowerCase() !== segs[i].toLowerCase()) { ok = false; break; }
      }
      if (!ok) continue;
      if (!path.includes(':')) return { entry, params };
      dynamic = dynamic ?? { entry, params };
    }
  }
  return dynamic;
};

// The key under which an admin override lives in mkt.seoPages: the sub path,
// with "/" for home (the server accepts both "" and "/").
export const seoKeyFor = (sub) => {
  const s = String(sub ?? '').replace(/^\/+|\/+$/g, '');
  return s || '/';
};

export const fillSeoTemplate = (tpl, vars) => String(tpl ?? '')
  .replace(/\{store\}/g, vars.store ?? '')
  .replace(/\{siteTitle\}/g, vars.siteTitle ?? '')
  .replace(/\{label\}/g, vars.label ?? '')
  // {brand} is the SHORT brand — prefer it over {siteTitle} in a page title.
  .replace(/\{brand\}/g, vars.brand ?? '');

// Built-in SEO for a page before any admin override. `vars` = { store,
// siteTitle, siteDesc }. Home keeps the store-level values verbatim.
// Where search results truncate a title.
export const SEO_TITLE_LIMIT = 60;

// brandShortOf — the brand on its own, for page titles. A site title carrying a
// tagline ("Adapt X.1 | Natural, Melatonin-Free Sleep Support" — 49 characters)
// pushed every page title past the truncation point, so the tagline was cut off
// on every page but the homepage. Admin override first, else the part before
// the first "|", else the store name.
export const brandShortOf = ({ brandShort, siteTitle, store } = {}) => {
  const explicit = String(brandShort ?? '').trim();
  if (explicit) return explicit;
  const t = String(siteTitle ?? '').trim();
  // A tagline is usually separated by a pipe or a SPACED dash ("Brand — Tag");
  // a hyphen inside a name ("Adapt-X") is part of the name.
  for (const sep of ['|', ' — ', ' – ', ' - ']) {
    const cut = t.indexOf(sep);
    if (cut > 0 && t.slice(0, cut).trim()) return t.slice(0, cut).trim();
  }
  return t || String(store ?? '').trim();
};

// composeTitle — "<page> | <brand>", dropping the suffix rather than emitting a
// title that would be cut mid-phrase, and never repeating a brand the page name
// already starts with ("Adapt X.1 | Adapt X.1 | …").
export const composeTitle = (label, brand) => {
  const l = String(label ?? '').trim();
  const b = String(brand ?? '').trim();
  if (!b || l.toLowerCase() === b.toLowerCase() || l.toLowerCase().startsWith(`${b.toLowerCase()} `)) return l;
  const full = `${l} | ${b}`;
  return [...full].length > SEO_TITLE_LIMIT ? l : full;
};

export const defaultSeoFor = (entry, vars) => {
  const label = entry?.label ?? '';
  const v = { ...vars, label, brand: brandShortOf(vars) };
  if (!entry || entry.key === 'landing') return { title: vars.siteTitle, description: vars.siteDesc, ogType: 'website' };
  const title = entry.title ? fillSeoTemplate(entry.title, v) : composeTitle(label, brandShortOf(vars));
  const description = entry.description ? fillSeoTemplate(entry.description, v) : vars.siteDesc;
  return { title, description, ogType: entry.ogType || 'website' };
};

// Scope choices for the Custom CSS / Custom HTML pickers: the pages this
// template actually renders, in registry order. Callers add 'all' / 'other'.
export const scopeOptions = (templateId, { storeType } = {}) => PAGES
  .filter((p) => implementedOn(p, templateId))
  .filter((p) => p.key !== 'portal' || storeType === 'hipaa')
  .filter((p) => !['register', 'sponsor', 'hrt-portal'].includes(p.key))
  .map((p) => ({ value: pageScope(p.key), label: p.key === 'landing' ? 'Homepage' : p.label }));

// Pages an admin can tune SEO for on a given template: indexable, implemented,
// static (no :params — products get their own rows from inventory).
export const seoEditablePages = (templateId) => PAGES
  .filter((p) => !p.private && implementedOn(p, templateId) && !String(p.path).includes(':'));
